/**
 * CLÁUSULAS ESTRUTURADAS DO CONTRATO — as variáveis que faziam falta.
 *
 * Até aqui o formulário coletava cliente, título, objeto e valor, e o resto do
 * contrato saía em frases genéricas: "no prazo acordado entre as partes", "na
 * forma e nas datas ajustadas". Prazo, multa, juros, sigilo, aviso prévio e
 * multa rescisória são justamente o que protege quem assina, e só existiam se o
 * prestador digitasse tudo à mão no corpo — que é o trabalho que ele veio
 * evitar.
 *
 * ─── AS TRÊS REGRAS QUE ESTE MÓDULO EXISTE PARA SEGURAR ─────────────────────
 *
 * 1. **Ausência NÃO é zero** (#61). Toda coluna é nullable e sem default:
 *    `null` = "o contrato não fala disso" e é o estado de TODO o histórico;
 *    `0` seria "multa de zero por cento", que é uma cláusula com significado
 *    OPOSTO. Os parsers devolvem `null` para vazio e recusam lixo em vez de
 *    convertê-lo — `Number('0x10')` é 16 e `Number('')` é 0 (#123a).
 *
 * 2. **Percentual tem parser PRÓPRIO** (#71/#157). O app já tem três parsers de
 *    propósito (dinheiro, custo, quantidade) e nenhum serve aqui: passar uma
 *    alíquota por `parseBRLInput` faria 2,5% virar 25%.
 *
 * 3. **Data aqui é DATE, e não instante** — `'YYYY-MM-DD'` sem fuso. A
 *    formatação é por PARTES da string, nunca `new Date(iso)`, que parseia como
 *    UTC e imprime o DIA ANTERIOR em UTC-3 (a família #114/#54/#219).
 *
 * A lista `CONTRACT_CLAUSE_COLUMNS` é a fonte única: os executores offline
 * ITERAM sobre ela em vez de repetir onze `assign()` à mão, que é a única forma
 * de eles não envelhecerem na próxima coluna (#89/#219).
 */

export interface ContractClauses {
  /** Onde o serviço será prestado. */
  executionPlace: string | null;
  /** Início da execução (DATE, 'YYYY-MM-DD'). */
  startDate: string | null;
  /** Término previsto (DATE, 'YYYY-MM-DD'). */
  endDate: string | null;
  /** Condições de pagamento em texto livre ("50% na assinatura e 50% na entrega"). */
  paymentTerms: string | null;
  /** Multa moratória, em % sobre o valor em atraso. */
  lateFeePercent: number | null;
  /** Juros de mora, em % ao mês. */
  interestPercent: number | null;
  /** Prazo de sigilo, em anos. */
  confidentialityYears: number | null;
  /** Aviso prévio para rescisão, em dias. */
  noticeDays: number | null;
  /** Multa rescisória, em % sobre o valor do contrato. */
  terminationFeePercent: number | null;
  /** Comarca do foro de eleição. */
  forumCity: string | null;
  /** Cidade onde o contrato é assinado (fecho do documento). */
  signingCity: string | null;
}

/**
 * Coluna do banco ↔ campo do objeto. É a fonte ÚNICA da lista, e existe para os
 * executores offline iterarem em vez de repetir onze `assign()` (#89/#219).
 */
export const CONTRACT_CLAUSE_COLUMNS: ReadonlyArray<{
  column: string;
  field: keyof ContractClauses;
}> = [
  { column: 'execution_place', field: 'executionPlace' },
  { column: 'start_date', field: 'startDate' },
  { column: 'end_date', field: 'endDate' },
  { column: 'payment_terms', field: 'paymentTerms' },
  { column: 'late_fee_percent', field: 'lateFeePercent' },
  { column: 'interest_percent', field: 'interestPercent' },
  { column: 'confidentiality_years', field: 'confidentialityYears' },
  { column: 'notice_days', field: 'noticeDays' },
  { column: 'termination_fee_percent', field: 'terminationFeePercent' },
  { column: 'forum_city', field: 'forumCity' },
  { column: 'signing_city', field: 'signingCity' },
];

export const EMPTY_CONTRACT_CLAUSES: ContractClauses = {
  executionPlace: null,
  startDate: null,
  endDate: null,
  paymentTerms: null,
  lateFeePercent: null,
  interestPercent: null,
  confidentialityYears: null,
  noticeDays: null,
  terminationFeePercent: null,
  forumCity: null,
  signingCity: null,
};

/** Texto vazio/em branco vira `null` — ausência, nunca string vazia. */
export function parseClauseText(raw: string | null | undefined): string | null {
  const t = String(raw ?? '').trim();
  return t.length ? t : null;
}

/**
 * Percentual em pt-BR ("5", "2,5"). Devolve `null` para ausência E para lixo:
 * um percentual ilegível não pode virar 0, que é uma cláusula válida com o
 * sentido oposto. Teto de 100 porque nenhuma destas cláusulas passa disso.
 */
export function parseClausePercent(raw: string | number | null | undefined): number | null {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw >= 0 && raw <= 100 ? raw : null;
  }
  const t = String(raw ?? '').trim();
  if (!t) return null;
  // Recusa notação exótica que o `Number` aceitaria ('0x10' → 16, '1e3' → 1000).
  if (!/^\d+(,\d+)?$/.test(t)) return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
}

/** Inteiro não negativo (anos de sigilo, dias de aviso). Lixo vira `null`. */
export function parseClauseInteger(raw: string | number | null | undefined): number | null {
  if (typeof raw === 'number') {
    return Number.isInteger(raw) && raw >= 0 ? raw : null;
  }
  const t = String(raw ?? '').trim();
  if (!t) return null;
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
}

/** `'YYYY-MM-DD'` válido, ou `null`. Não aceita instante — a coluna é DATE. */
export function parseClauseDate(raw: string | null | undefined): string | null {
  const t = String(raw ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
  const [ano, mes, dia] = t.split('-').map(Number);
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
  // Rejeita 31/02 e afins sem construir Date no fuso local.
  const ultimoDia = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  return dia <= ultimoDia ? t : null;
}

/**
 * `'2026-08-11'` → `'11/08/2026'`, por PARTES da string.
 *
 * `new Date('2026-08-11').toLocaleDateString('pt-BR')` devolveria **10/08/2026**
 * em UTC-3: a string sem fuso é parseada como meia-noite UTC, que ainda é o dia
 * anterior no Brasil. É o #114 num documento com valor probatório.
 */
export function formatClauseDate(iso: string | null | undefined): string | null {
  const ok = parseClauseDate(iso);
  if (!ok) return null;
  const [ano, mes, dia] = ok.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** `5` → `'5%'`; `2.5` → `'2,5%'`. Sem casas inúteis no texto do contrato. */
export function formatClausePercent(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  const texto = Number.isInteger(value)
    ? String(value)
    : String(Number(value.toFixed(2))).replace('.', ',');
  return `${texto}%`;
}

/** Plural pt-BR simples — "1 dia" / "30 dias" (#87a). */
export function pluralizeUnit(value: number, singular: string, plural: string): string {
  return `${value} ${value === 1 ? singular : plural}`;
}

/** Constrói o objeto de colunas para gravar. Fonte única de escrita (#69). */
export function buildContractClauseColumns(clauses: ContractClauses): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const { column, field } of CONTRACT_CLAUSE_COLUMNS) {
    row[column] = clauses[field] ?? null;
  }
  return row;
}

/**
 * LEITURA — a metade que o guard de escrita nunca alcança (#70). Sem ela o
 * próximo save grava `null` por cima e as cláusulas somem sozinhas.
 */
export function contractClausesFromRow(row: Record<string, unknown> | null | undefined): ContractClauses {
  if (!row) return { ...EMPTY_CONTRACT_CLAUSES };
  return {
    executionPlace: parseClauseText(row.execution_place as string),
    startDate: parseClauseDate(row.start_date as string),
    endDate: parseClauseDate(row.end_date as string),
    paymentTerms: parseClauseText(row.payment_terms as string),
    lateFeePercent: parseClausePercent(row.late_fee_percent as number),
    interestPercent: parseClausePercent(row.interest_percent as number),
    confidentialityYears: parseClauseInteger(row.confidentiality_years as number),
    noticeDays: parseClauseInteger(row.notice_days as number),
    terminationFeePercent: parseClausePercent(row.termination_fee_percent as number),
    forumCity: parseClauseText(row.forum_city as string),
    signingCity: parseClauseText(row.signing_city as string),
  };
}

// ─── Nível FORMULÁRIO: tudo é texto até a gravação ──────────────────────────

/** Os campos do formulário são os mesmos do objeto — todos como TEXTO. */
export type ClauseField = keyof ContractClauses;

export type ClauseForm = Record<ClauseField, string>;

export const EMPTY_CLAUSE_FORM: ClauseForm = CONTRACT_CLAUSE_COLUMNS.reduce((acc, { field }) => {
  acc[field] = '';
  return acc;
}, {} as ClauseForm);

/** Texto do formulário → cláusulas parseadas. Lixo e vazio viram `null`. */
export function clausesFromForm(form: ClauseForm): ContractClauses {
  return {
    executionPlace: parseClauseText(form.executionPlace),
    startDate: parseClauseDate(form.startDate),
    endDate: parseClauseDate(form.endDate),
    paymentTerms: parseClauseText(form.paymentTerms),
    lateFeePercent: parseClausePercent(form.lateFeePercent),
    interestPercent: parseClausePercent(form.interestPercent),
    confidentialityYears: parseClauseInteger(form.confidentialityYears),
    noticeDays: parseClauseInteger(form.noticeDays),
    terminationFeePercent: parseClausePercent(form.terminationFeePercent),
    forumCity: parseClauseText(form.forumCity),
    signingCity: parseClauseText(form.signingCity),
  };
}

/**
 * Cláusulas → texto do formulário (hidratação da edição).
 *
 * Par OBRIGATÓRIO de `clausesFromForm`: hidratar de um jeito e ler de outro
 * corrompe o valor na primeira edição, que é a lição do #153a — aqui o risco é
 * o percentual, que sai do Postgres como `2.5` e precisa entrar no campo como
 * `"2,5"` para o parser pt-BR reconhecê-lo.
 */
export function clauseFormFromClauses(clauses: ContractClauses): ClauseForm {
  const texto = (v: string | null) => v ?? '';
  const numero = (v: number | null) => (v == null ? '' : String(v).replace('.', ','));
  return {
    executionPlace: texto(clauses.executionPlace),
    startDate: texto(clauses.startDate),
    endDate: texto(clauses.endDate),
    paymentTerms: texto(clauses.paymentTerms),
    lateFeePercent: numero(clauses.lateFeePercent),
    interestPercent: numero(clauses.interestPercent),
    confidentialityYears: numero(clauses.confidentialityYears),
    noticeDays: numero(clauses.noticeDays),
    terminationFeePercent: numero(clauses.terminationFeePercent),
    forumCity: texto(clauses.forumCity),
    signingCity: texto(clauses.signingCity),
  };
}

/** Alguma cláusula foi informada? Usado para decidir avisos na tela. */
export function hasAnyClause(clauses: ContractClauses): boolean {
  return CONTRACT_CLAUSE_COLUMNS.some(({ field }) => clauses[field] != null);
}

/**
 * Validação de COERÊNCIA entre campos, para a tela avisar em pt-BR antes de
 * gravar.
 *
 * Deliberadamente NÃO existe um CHECK `end_date >= start_date` no banco: o
 * caminho offline enfileira a operação, e uma recusa do Postgres ali deixaria a
 * op presa na fila para sempre, travando os outros módulos atrás dela (#123d).
 * A regra vive aqui, onde os dois caminhos passam.
 */
export function validateClauses(clauses: ContractClauses): string | null {
  if (clauses.startDate && clauses.endDate && clauses.endDate < clauses.startDate) {
    return 'A data de término não pode ser anterior à de início.';
  }
  return null;
}
